import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/http.js';
import { useTheme } from '../../hooks/useTheme.jsx';
import { isNative } from '../../services/platform.js';

const ForgotPasswordPage = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const native = isNative();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('request'); // 'request' | 'confirm' (nativo apenas)
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const { res } = await apiClient.request('/auth/password-reset-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        if (native) {
          setStep('confirm');
        } else {
          setInfo('Se o email existir, enviamos as instruções.');
        }
      } else {
        setError('Falha ao solicitar recuperação.');
      }
    } catch {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const { res, data } = await apiClient.confirmPasswordResetCode({ email, code, new_password: password });
      if (res.ok) {
        setInfo('Senha atualizada com sucesso! Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(data?.detail || 'Código inválido ou expirado.');
      }
    } catch {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center relative overflow-hidden bg-background p-4">
      <div className="fixed top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-primary-container/5 blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-primary-container/5 blur-[100px] pointer-events-none"></div>

      <div className="fixed top-4 left-4 right-4 flex justify-between z-20">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors text-sm">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="hidden sm:inline">Início</span>
        </button>
        <button onClick={toggleTheme} className="p-2 rounded-full text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-all">
          <span className="material-symbols-outlined text-[20px]">{theme === 'dark' ? 'light_mode' : 'dark_mode'}</span>
        </button>
      </div>

      <main className="relative z-10 w-full max-w-md px-6 py-12 flex flex-col items-center">
        <div className="mb-8 flex flex-col items-center opacity-80">
          <img alt="CatholicGPT Logo" className="h-16 w-16 mx-auto rounded-full bg-white object-contain p-2 shadow-sm mb-4" src="/logo-192.png"/>
        </div>

        <div className="w-full glass-panel rounded-xl border border-outline-variant/20 shadow-2xl p-8">
          {step === 'request' ? (
            <>
              <h2 className="font-headline text-2xl font-bold text-on-surface mb-4 text-center">Recuperar Senha</h2>
              <p className="text-sm text-on-surface-variant mb-6 text-center">
                {native ? 'Enviaremos um código de 6 dígitos para o seu email.' : 'Enviaremos instruções para o seu email.'}
              </p>
              <form onSubmit={handleRequest} className="flex flex-col gap-4">
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">mail</span>
                  <input
                    className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 pl-10 pr-4 py-3 text-on-surface outline-none focus:border-primary transition-colors"
                    placeholder="Seu email" required type="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {error && <p className="text-error text-sm text-center">{error}</p>}
                {info && <p className="text-primary text-sm text-center">{info}</p>}
                <button
                  className="w-full bg-primary-container hover:bg-primary text-on-primary font-medium py-3 rounded-full mt-2 transition-all disabled:opacity-50"
                  type="submit" disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Enviar Instruções'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-headline text-2xl font-bold text-on-surface mb-2 text-center">Digite o Código</h2>
              <p className="text-on-surface-variant text-sm text-center mb-6">
                Enviamos um código de 6 dígitos para {email}. Digite abaixo com sua nova senha.
              </p>
              <form onSubmit={handleConfirm} className="flex flex-col gap-4">
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 px-4 py-3 text-on-surface outline-none focus:border-primary text-center text-lg tracking-widest transition-colors"
                  placeholder="000000" required type="text" maxLength={6}
                  autoComplete="one-time-code"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">lock</span>
                  <input
                    className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 pl-10 pr-4 py-3 text-on-surface outline-none focus:border-primary transition-colors"
                    placeholder="Nova senha" required type="password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                  />
                </div>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">lock</span>
                  <input
                    className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 pl-10 pr-4 py-3 text-on-surface outline-none focus:border-primary transition-colors"
                    placeholder="Confirmar nova senha" required type="password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                  />
                </div>
                {error && <p className="text-error text-sm text-center">{error}</p>}
                {info && <p className="text-primary text-sm text-center">{info}</p>}
                <button
                  className="w-full bg-primary-container hover:bg-primary text-on-primary font-medium py-3 rounded-full mt-2 transition-all disabled:opacity-50"
                  type="submit" disabled={loading || code.length !== 6}
                >
                  {loading ? 'Confirmando...' : 'Redefinir Senha'}
                </button>
              </form>
            </>
          )}
          <div className="mt-6 text-center">
            <Link className="text-sm text-primary font-medium" to="/login">Voltar para Login</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
